import { isNil } from 'lodash'
import { useState, useCallback, useEffect, useRef } from 'react'
import { PaymentCase, PaymentStatus, Priority } from '../Utils/types'
import type { GetPaymentAssignmentResponseDto } from '../../../api/dto/get-payment-assignment.dto'

interface UseCasesOptions {
  queryData: GetPaymentAssignmentResponseDto | undefined
  isPaymentAssignmentFetched: boolean
  isPaymentAssignmentError: boolean
  requestKey: string
}

export function usePaymentAssignmentCases({
  queryData,
  isPaymentAssignmentFetched,
  isPaymentAssignmentError,
  requestKey,
}: UseCasesOptions) {
  const [cases, setCases] = useState<PaymentCase[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  const lastRequestKeyRef = useRef(requestKey)

  // Clear stale cases immediately when the request changes
  useEffect(() => {
    if (requestKey !== lastRequestKeyRef.current) {
      lastRequestKeyRef.current = requestKey
      setCases([])
      setSelectedRowKeys([])
    }
  }, [requestKey])

  // Sync query results into local state
  useEffect(() => {
    if (queryData?.data) {
      setCases(queryData.data)
      return
    }
    if (isPaymentAssignmentError || isPaymentAssignmentFetched) {
      setCases([])
    }
  }, [queryData, isPaymentAssignmentError, isPaymentAssignmentFetched])

  const isStaleRequest = requestKey !== lastRequestKeyRef.current
  const casesForActiveRequest = isStaleRequest ? [] : cases

  // Drop selections for rows that are no longer visible after a filter change
  useEffect(() => {
    const visibleIdSet = new Set(casesForActiveRequest.map((c) => c.benefitId))
    setSelectedRowKeys((prev) => prev.filter((id) => visibleIdSet.has(id)))
  }, [casesForActiveRequest])

  const handleRowSelectionChange = useCallback((keys: string[]) => {
    setSelectedRowKeys(keys)
  }, [])

  const updateCaseStatus = useCallback(
    (caseId: number, paymentStatus: PaymentStatus, _notes?: string) => {
      setCases((prevCases) =>
        prevCases.map((c) =>
          String(c.id) === String(caseId)
            ? {
                ...c,
                paymentStatus: {
                  ...(c.paymentStatus ?? {}),
                  id: paymentStatus,
                  description: isNil(c.paymentStatus?.description)
                    ? null
                    : c.paymentStatus?.description,
                },
              }
            : c
        )
      )
    },
    []
  )

  const bulkAssignCases = useCallback(
    (
      caseIds: string[],
      assigneeId: number | null | undefined,
      escalatorId: number | null | undefined,
      priority: Priority | null,
      _notes?: string
    ) => {
      setCases((prevCases) =>
        prevCases.map((c) =>
          caseIds.includes(c.benefitId)
            ? {
                ...c,
                // undefined = no change; null = unassign (clear); number = assign
                assigneeId:
                  assigneeId === undefined
                    ? c.assigneeId
                    : (assigneeId ?? undefined),
                assigneeName:
                  assigneeId === undefined
                    ? c.assigneeName
                    : assigneeId === null
                      ? undefined // unassign → clear name
                      : c.assigneeName, // assign → keep until refetch
                escalatorId:
                  escalatorId === undefined
                    ? c.escalatorId
                    : (escalatorId ?? undefined),
                escalatorName:
                  escalatorId === undefined
                    ? c.escalatorName
                    : escalatorId === null
                      ? null // unassign → clear name
                      : c.escalatorName, // assign → keep until refetch
                priority: priority ?? c.priority,
              }
            : c
        )
      )
    },
    []
  )

  const updateCaseEscalator = useCallback(
    (caseId: number, escalatorId: number | null, escalatorName?: string) => {
      setCases((prevCases) =>
        prevCases.map((paymentCase) =>
          String(paymentCase.id) === String(caseId)
            ? {
                ...paymentCase,
                escalatorId: escalatorId ?? undefined,
                escalatorName:
                  escalatorId != null ? (escalatorName ?? undefined) : null,
              }
            : paymentCase
        )
      )
    },
    []
  )

  const removeCaseById = useCallback((caseId: number) => {
    setCases((prevCases) =>
      prevCases.filter(
        (paymentCase) => String(paymentCase.id) !== String(caseId)
      )
    )
  }, [])

  return {
    cases: casesForActiveRequest,
    selectedRowKeys,
    handleRowSelectionChange,
    updateCaseStatus,
    bulkAssignCases,
    updateCaseEscalator,
    removeCaseById,
    setCases,
  }
}
